import React from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, Mail } from 'lucide-react'
import Header from './Header'
import Footer from './Footer'
import BrandIcon from './BrandIcon'
import { SOCIALS } from '../data/socials'

/**
 * Lists every official SafePsy profile in one place, so the footer only has to
 * carry a single "Socials" link instead of one row per network.
 */
const Socials: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main id="main-content" className="flex-1" role="main" aria-label="SafePsy socials" tabIndex={-1}>
        <section className="section-padding py-8 lg:py-12">
          <div className="container-max">
            <div className="text-center mb-10 sm:mb-14 px-4 fade-in">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl text-heading leading-tight mb-4">
                <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent font-bold">
                  Find SafePsy
                </span>
              </h1>
              <p className="text-body text-sm sm:text-base max-w-2xl mx-auto">
                Every place you can find us, in one list. These are our only official accounts.
              </p>
            </div>

            <ul className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mb-12 sm:mb-16 list-none p-0">
              {SOCIALS.map((social) => (
                <li key={social.url}>
                  <a
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-full flex items-start gap-4 p-5 sm:p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:scale-[1.01] group"
                    aria-label={`${social.name} — ${social.handle} (opens in new tab)`}
                  >
                    <BrandIcon
                      icon={social.icon}
                      className="w-7 h-7 flex-shrink-0 text-primary-600 dark:text-primary-400 transition-transform duration-300 group-hover:scale-110"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="text-heading font-semibold text-base sm:text-lg">
                          {social.name}
                        </span>
                        <ExternalLink
                          className="w-3.5 h-3.5 flex-shrink-0 text-text-primary/50 dark:text-white/50"
                          aria-hidden="true"
                        />
                      </span>
                      <span className="block text-primary-600 dark:text-primary-400 font-titillium text-sm sm:text-base truncate">
                        {social.handle}
                      </span>
                      <span className="block text-body text-xs sm:text-sm mt-1.5">
                        {social.description}
                      </span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>

            <div className="text-center">
              <p className="text-body text-sm sm:text-base mb-4">
                Cannot find what you need? Reach us directly instead.
              </p>
              <Link
                to="/contact-us"
                className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 min-h-[44px] inline-flex items-center justify-center gap-2 hover:scale-105 active:scale-95 transition-transform duration-200"
              >
                <Mail className="w-5 h-5" aria-hidden="true" />
                Contact us
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default Socials
